import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button,
  Dialog, DialogTitle, DialogContent, Switch, IconButton, Tabs, Tab
} from '@mui/material';
import { 
  AccountBalanceWalletOutlined, CalendarMonthOutlined, Logout, 
  SettingsOutlined, AccessTimeOutlined, NotificationsActiveOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAuthState, getFirebaseFunctions, firebaseGoogleSignOut, app } from '../firebase/authHandler';
import { httpsCallable } from 'firebase/functions';
import { requestNotificationPermission } from '../firebase/pushNotifications';
import AdBanner from '../components/AdBanner';

function fmt(num) {
  return Number(num || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
}

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CustomerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [viewMode, setViewMode] = useState('financier'); // 'financier', 'due', 'all'
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = getAuthState().onAuthStateChanged(user => {
      if (!user || !user.phoneNumber) {
        navigate('/customer-login');
      } else {
        fetchDashboardData();
        setTimeout(() => requestNotificationPermission(app, 'customer'), 2000);
      }
    });
    return unsubscribe;
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      const functions = getFirebaseFunctions();
      const getCustomerDashboard = httpsCallable(functions, 'getCustomerDashboard');
      const result = await getCustomerDashboard();
      setData(result.data);
      setRemindersEnabled(result.data.preferences?.remindersEnabled !== false);
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await firebaseGoogleSignOut();
    navigate('/customer-login');
  };

  const toggleReminders = async () => {
    const newVal = !remindersEnabled;
    setRemindersEnabled(newVal);
    setUpdatingSettings(true);
    try {
      const functions = getFirebaseFunctions();
      const updatePrefs = httpsCallable(functions, 'updateCustomerPreferences');
      await updatePrefs({ remindersEnabled: newVal });
    } catch (err) {
      console.error("Failed to update preferences", err);
      setRemindersEnabled(!newVal); // revert on error
    }
    setUpdatingSettings(false);
  };

  const processedAccounts = useMemo(() => {
    if (!data?.accounts) return [];
    return data.accounts.map(acc => {
      const paidAmount = acc.entries ? Object.values(acc.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.amountPaid || 0)), 0) : 0;
      const paidDays = acc.entries ? Object.values(acc.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.dayCount || 0)), 0) : 0;
      const expectedTotal = (acc.installmentAmount * acc.installmentPeriod) || (Number(acc.amount) + Number(acc.interestAmount || 0));
      const balance = expectedTotal - paidAmount;
      const progress = acc.installmentPeriod > 0 ? Math.min(100, Math.round((paidDays / acc.installmentPeriod) * 100)) : 0;
      
      let dueStatus = 'Up to Date';
      let dueStatusColor = '#0fd47a';
      
      // Determine due status
      if (balance > 0) {
        const lastEntryDate = acc.entries ? Math.max(...Object.values(acc.entries).map(e => new Date(e.date).getTime())) : new Date(acc.date).getTime();
        const daysSinceLastPayment = Math.floor((new Date().getTime() - lastEntryDate) / (1000 * 60 * 60 * 24));
        
        if ((acc.installmentType === 'day' || acc.installmentType === 'daily') && daysSinceLastPayment > 1) {
            dueStatus = 'Overdue';
            dueStatusColor = '#ef5350';
        } else if (daysSinceLastPayment === 1 || daysSinceLastPayment === 0) {
            dueStatus = 'Due Today';
            dueStatusColor = '#ffb74d';
        }
      } else {
         dueStatus = 'Completed';
         dueStatusColor = '#8892b0';
      }

      return { ...acc, paidAmount, paidDays, balance, progress, dueStatus, dueStatusColor };
    });
  }, [data]);

  const portfolioTotals = useMemo(() => {
    let totalPrincipal = 0;
    let totalPaid = 0;
    let totalBalance = 0;
    
    processedAccounts.forEach(acc => {
      totalPrincipal += Number(acc.amount || 0);
      totalPaid += Number(acc.paidAmount || 0);
      totalBalance += Number(acc.balance || 0);
    });
    
    const progress = totalPrincipal > 0 ? Math.min(100, Math.round((totalPaid / totalPrincipal) * 100)) : 0;
    return { totalPrincipal, totalPaid, totalBalance, progress };
  }, [processedAccounts]);

  const renderAccountCard = (account, idx) => (
    <Box key={idx} sx={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, mb: 4, overflow: 'hidden',
    }}>
      <Box sx={{
        background: 'linear-gradient(135deg, rgba(45,132,255,0.12), rgba(37,212,255,0.06))', borderBottom: '1px solid rgba(255,255,255,0.06)',
        px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AccountBalanceWalletOutlined sx={{ color: 'var(--accent-blue)', fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)', fontFamily: 'Inter' }}>
              A/C {account.accountId ? account.accountId.split('_')[0] : '—'}
            </Typography>
            <Box sx={{ ml: 1, background: account.dueStatusColor + '20', color: account.dueStatusColor, px: 1, py: 0.25, borderRadius: 1, fontSize: 10, fontWeight: 700, border: `1px solid ${account.dueStatusColor}40` }}>
               {account.dueStatus}
            </Box>
          </Box>
          <Typography sx={{ color: 'var(--accent-teal)', fontSize: 12, fontFamily: 'Inter', fontWeight: 600 }}>
            Partner: {account.financierName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthOutlined sx={{ color: '#64748b', fontSize: 16 }} />
          <Typography sx={{ color: '#8892b0', fontSize: 13, fontFamily: 'Inter' }}>
            Since {fmtDate(account.date)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 3 }}>
        {[
          { label: 'Principal Amount', value: `₹ ${fmt(account.amount)}`, color: 'var(--text-primary)' },
          { label: 'Interest', value: `₹ ${fmt(account.interestAmount)} (${account.interestPercentage}%)`, color: 'var(--text-primary)' },
          { label: 'Installment', value: `₹ ${fmt(account.installmentAmount)} / ${account.installmentType || 'day'}`, color: 'var(--text-primary)' },
          { label: 'Total Recovered', value: `₹ ${fmt(account.paidAmount)}`, color: 'var(--accent-green)' },
          { label: 'Outstanding Balance', value: `₹ ${fmt(account.balance)}`, color: account.balance > 0 ? '#ef5350' : 'var(--accent-green)' },
        ].map(({ label, value, color }) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
            <Typography sx={{ color: '#8892b0', fontSize: 14, fontFamily: 'Inter' }}>{label}</Typography>
            <Typography sx={{ color, fontSize: 14, fontWeight: 600, fontFamily: 'Inter' }}>{value}</Typography>
          </Box>
        ))}

        <Box sx={{ mt: 3, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 12, fontFamily: 'Inter' }}>Repayment Progress</Typography>
            <Typography sx={{ color: 'var(--accent-blue)', fontSize: 12, fontWeight: 700, fontFamily: 'Inter' }}>{account.progress}%</Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 10, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', width: `${account.progress}%`, borderRadius: 10,
              background: account.progress >= 100 ? 'linear-gradient(90deg, var(--accent-green), #0a9b58)' : 'linear-gradient(90deg, var(--accent-blue), var(--accent-teal))',
              transition: 'width 0.6s ease',
            }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
            <Typography sx={{ color: '#8892b0', fontSize: 11, fontFamily: 'Inter' }}>{account.paidDays} {account.installmentType || 'days'} paid</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 11, fontFamily: 'Inter' }}>{account.installmentPeriod} total</Typography>
          </Box>
        </Box>

        {account.entries && Object.keys(account.entries).length > 0 && (
          <>
            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.06)' }} />
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#f0f4ff', fontFamily: 'Inter', mb: 2 }}>Payment History</Typography>
            <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(255,255,255,0.06)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: 'rgba(255,255,255,0.02)' }}>
                    <TableCell sx={{ color: '#64748b', fontSize: 11, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Date</TableCell>
                    <TableCell sx={{ color: '#64748b', fontSize: 11, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Paid For</TableCell>
                    <TableCell align="right" sx={{ color: '#64748b', fontSize: 11, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.values(account.entries).sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10).map((entry, eIdx) => (
                    <TableRow key={eIdx}>
                      <TableCell sx={{ color: '#f0f4ff', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>{fmtDate(entry.date)}</TableCell>
                      <TableCell sx={{ color: '#8892b0', fontSize: 13, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>{entry.isUpfrontInterest ? 'Upfront' : `${entry.dayCount} ${account.installmentType || 'days'}`}</TableCell>
                      <TableCell align="right" sx={{ color: '#0fd47a', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.03)' }}>₹ {fmt(entry.amountPaid)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {Object.values(account.entries).length > 10 && (
               <Typography sx={{ color: '#64748b', fontSize: 11, mt: 1, textAlign: 'center' }}>Showing last 10 entries</Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  const renderGroupedAccounts = () => {
    if (processedAccounts.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: 3, border: '1px dashed rgba(255,255,255,0.1)' }}>
          <Typography sx={{ color: '#8892b0', fontFamily: 'Inter' }}>No active accounts found linked to this number.</Typography>
        </Box>
      );
    }

    if (viewMode === 'all') {
      return processedAccounts.map((acc, idx) => renderAccountCard(acc, idx));
    }

    if (viewMode === 'financier') {
      const grouped = {};
      processedAccounts.forEach(acc => {
        if (!grouped[acc.financierName]) grouped[acc.financierName] = [];
        grouped[acc.financierName].push(acc);
      });
      return Object.entries(grouped).map(([finName, accs]) => (
        <Box key={finName} sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 1 }}>
             {finName}
          </Typography>
          {accs.map((acc, idx) => renderAccountCard(acc, idx))}
        </Box>
      ));
    }

    if (viewMode === 'due') {
      const grouped = { 'Overdue': [], 'Due Today': [], 'Up to Date': [], 'Completed': [] };
      processedAccounts.forEach(acc => {
        grouped[acc.dueStatus].push(acc);
      });
      return Object.entries(grouped).map(([status, accs]) => {
        if (accs.length === 0) return null;
        let color = '#fff';
        if (status === 'Overdue') color = '#ef5350';
        if (status === 'Due Today') color = '#ffb74d';
        if (status === 'Completed') color = '#8892b0';

        return (
          <Box key={status} sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2, color, borderBottom: `1px solid ${color}40`, pb: 1 }}>
               {status} ({accs.length})
            </Typography>
            {accs.map((acc, idx) => renderAccountCard(acc, idx))}
          </Box>
        );
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
        <CircularProgress sx={{ color: 'var(--accent-blue)' }} />
      </Box>
    );
  }

  if (error) {
    return (
    <Box sx={{
      height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-gradient)',
      color: 'var(--text-primary)', p: 3, textAlign: 'center'
    }}>
        <Typography sx={{ color: '#ef5350', fontSize: 16, fontFamily: 'Inter', mb: 2 }}>{error}</Typography>
        <Button variant="outlined" onClick={handleLogout} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}>
          Sign Out & Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, overflowY: 'auto', background: 'var(--bg-base)', color: 'var(--text-primary)', pb: 10 }}>
      <Box sx={{ maxWidth: '800px', margin: '0 auto', p: { xs: 2, md: 4 } }}>
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, background: 'var(--bg-surface)', p: 3, borderRadius: 4, border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-sm)' }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'Inter', letterSpacing: '-0.02em' }}>
              My Dashboard
            </Typography>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'Inter', mt: 0.5 }}>
              Logged in as {getAuthState().currentUser?.phoneNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
             <IconButton onClick={() => setSettingsOpen(true)} sx={{ color: 'var(--text-secondary)', background: 'var(--bg-card-hover)' }}>
               <SettingsOutlined fontSize="small" />
             </IconButton>
             <IconButton onClick={handleLogout} sx={{ color: 'var(--text-secondary)', background: 'var(--bg-card-hover)' }}>
               <Logout fontSize="small" />
             </IconButton>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <AdBanner type="horizontal" />
        </Box>

        {/* Hero Card: Overall Portfolio */}
        <Box sx={{
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 4, p: 3, mb: 4, position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-md)'
        }}>
           <Box sx={{ position: 'absolute', right: -20, top: -20, opacity: 0.05, color: 'var(--text-primary)' }}>
              <AccountBalanceWalletOutlined sx={{ fontSize: 150 }} />
           </Box>
           <Typography sx={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1 }}>
              Total Portfolio Balance
           </Typography>
           <Typography sx={{ fontSize: 36, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.03em', mb: 3 }}>
              ₹ {fmt(portfolioTotals.totalBalance)}
           </Typography>
           
           <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Borrowed</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>₹ {fmt(portfolioTotals.totalPrincipal)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>Total Recovered</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'var(--accent-green)' }}>₹ {fmt(portfolioTotals.totalPaid)}</Typography>
              </Box>
           </Box>

           <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                 <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)' }}>Overall Progress</Typography>
                 <Typography sx={{ fontSize: 11, color: 'var(--text-primary)', fontWeight: 700 }}>{portfolioTotals.progress}%</Typography>
              </Box>
              <Box sx={{ height: 6, borderRadius: 3, background: 'var(--bg-card-hover)', overflow: 'hidden' }}>
                 <Box sx={{ width: `${portfolioTotals.progress}%`, height: '100%', background: 'var(--accent-green)', borderRadius: 3 }} />
              </Box>
           </Box>
        </Box>

        {/* Next Installments Preview */}
        {processedAccounts.filter(a => a.balance > 0).length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeOutlined fontSize="small" sx={{ color: '#ffb74d' }} /> Upcoming Installments
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
              {processedAccounts.filter(a => a.balance > 0).map((acc, idx) => (
                <Box key={idx} sx={{ minWidth: 200, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 3, p: 2, flexShrink: 0 }}>
                   <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>{acc.financierName}</Typography>
                   <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', my: 1 }}>₹ {fmt(acc.installmentAmount)}</Typography>
                   <Typography sx={{ fontSize: 11, color: acc.dueStatusColor }}>{acc.dueStatus}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* View Mode Tabs */}
        <Box sx={{ mb: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <Tabs 
            value={viewMode} 
            onChange={(e, val) => setViewMode(val)} 
            textColor="inherit" 
            TabIndicatorProps={{ style: { background: 'var(--accent-teal)' } }}
          >
            <Tab label="By Financier" value="financier" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="By Due Status" value="due" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="All Loans" value="all" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>
        </Box>

        {/* Accounts List */}
        {renderGroupedAccounts()}

        <Box sx={{ mt: 4 }}>
          <AdBanner type="mobile" />
        </Box>
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} PaperProps={{ style: { background: '#1a2235', color: '#fff', borderRadius: 16, minWidth: 320 } }}>
         <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <SettingsOutlined /> App Settings
         </DialogTitle>
         <DialogContent sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
               <Box>
                  <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                     <NotificationsActiveOutlined fontSize="small" sx={{ color: '#25d4ff' }}/> Daily Reminders
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#8892b0', mt: 0.5 }}>
                     Receive a push notification every morning if you have installments due today.
                  </Typography>
               </Box>
               <Switch 
                  checked={remindersEnabled} 
                  onChange={toggleReminders} 
                  disabled={updatingSettings}
                  color="primary"
               />
            </Box>
            
            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

            <Box sx={{ mb: 2 }}>
               <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  Legal & Policies
               </Typography>
               <Box 
                 onClick={() => navigate('/terms')}
                 sx={{ 
                   display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                   background: 'var(--bg-card)', padding: '16px 20px', borderRadius: 4,
                   border: '1px solid var(--border-subtle)', cursor: 'pointer', mt: 1
                 }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                   <Box sx={{ background: 'rgba(15,212,122,0.1)', padding: 1, borderRadius: 2, display: 'flex' }}>
                     <Typography sx={{ fontSize: 22 }}>📜</Typography>
                   </Box>
                   <Box>
                     <Typography sx={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#fff', fontFamily: 'Inter' }}>Terms & Privacy Policy</Typography>
                     {data?.legal?.legalAcceptedVersion && data?.legal?.legalAcceptedAt ? (
                        <Typography sx={{ margin: '4px 0 0 0', fontSize: 11, color: '#0fd47a', fontFamily: 'Inter', fontWeight: 600, background: 'rgba(15,212,122,0.1)', padding: '2px 8px', borderRadius: 10, display: 'inline-block' }}>
                          ✅ Accepted {data.legal.legalAcceptedVersion} on {new Date(data.legal.legalAcceptedAt).toLocaleDateString()}
                        </Typography>
                     ) : (
                        <Typography sx={{ margin: 0, fontSize: 12, color: '#8892b0', fontFamily: 'Inter' }}>Review our legal documents</Typography>
                     )}
                   </Box>
                 </Box>
               </Box>
            </Box>

         </DialogContent>
      </Dialog>
    </Box>
  );
}
